import * as React from "react";
import { Loader2, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useCreateMailbox } from "@/hooks/useMailboxes";
import { ApiError } from "@/lib/api";

interface AddMailboxDialogProps {
  containerId: number;
  domainName: string;
  /** When true the plan's mailbox pool is exhausted; the trigger is disabled. */
  atLimit?: boolean;
}

export function AddMailboxDialog({ containerId, domainName, atLimit = false }: AddMailboxDialogProps) {
  const [open, setOpen] = React.useState(false);
  const [localPart, setLocalPart] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [quotaMb, setQuotaMb] = React.useState("1024");
  const [displayName, setDisplayName] = React.useState("");

  const { toast } = useToast();
  const create = useCreateMailbox(containerId);

  function reset() {
    setLocalPart("");
    setPassword("");
    setQuotaMb("1024");
    setDisplayName("");
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await create.mutateAsync({
        local_part: localPart.trim().toLowerCase(),
        password,
        quota_mb: Number(quotaMb) || 1024,
        display_name: displayName.trim() || undefined
      });
      toast({
        title: "Mailbox created",
        description: `${localPart}@${domainName} is ready.`,
        variant: "success"
      });
      setOpen(false);
      reset();
    } catch (err) {
      if (err instanceof ApiError) {
        const friendlyTitle =
          err.code === "package_not_found"
            ? "Hosting not provisioned"
            : err.code === "already_exists"
              ? "Mailbox already exists"
              : "Couldn't create mailbox";
        const description =
          err.code === "package_not_found"
            ? `${domainName} doesn't have a 20i hosting package yet — finish the order first.`
            : err.message;
        toast({ title: friendlyTitle, description, variant: "destructive" });
      } else {
        toast({
          title: "Couldn't create mailbox",
          description: err instanceof Error ? err.message : "Try again.",
          variant: "destructive"
        });
      }
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" disabled={atLimit} title={atLimit ? "Mailbox pool full — delete one or upgrade your plan" : undefined}>
          <Plus className="h-4 w-4" />
          New mailbox
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New mailbox</DialogTitle>
          <DialogDescription>
            Provisioned on 20i. Counts against your domain's mailbox allocation.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="localPart">Address</Label>
            <div className="flex items-stretch overflow-hidden rounded-md border border-input focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
              <Input
                id="localPart"
                required
                autoFocus
                value={localPart}
                onChange={e => setLocalPart(e.target.value)}
                placeholder="info"
                className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <span className="flex items-center bg-muted px-3 text-sm text-muted-foreground">
                @{domainName}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              required
              autoComplete="new-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              The user can change this after logging in to webmail.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="quotaMb">Quota (MB)</Label>
              <Input
                id="quotaMb"
                type="number"
                inputMode="numeric"
                min={64}
                step={64}
                value={quotaMb}
                onChange={e => setQuotaMb(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="displayName">Display name <span className="font-normal text-muted-foreground">(optional)</span></Label>
              <Input
                id="displayName"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                placeholder="Customer Support"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={create.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || !localPart.trim() || !password}>
              {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Create mailbox
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
